import React from "react";
import shallowCompare from "react-addons-shallow-compare";
import Helmet from "react-helmet";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { StyleRoot } from "radium";
import Sticky from "react-stickynode";
import Waypoint from "react-waypoint";
import BookmarkButton from "backpack-ui/dist/components/bookmarkButton";
import PageHeader from "backpack-ui/dist/components/pageHeader";
import ShareButton from "./shareButton";
import Breadcrumbs from "./breadcrumbs";
import ContentBlock from "./contentBlock";
import EssentialInfo from "./essentialInfo";
import ContentHeader from "./contentHeader";
import { connected as NearbyPlaces } from "./nearbyPlaces";
import Callout from "./callout";
import EditLink from "./editLink";
import ImagesContainer from "./imagesContainer";
import ContactBar from "./contactBar";
import FeatureDiscover from "./featureDiscover";
import DiscoveryFlyout from "./featureDiscover/discoveryFlyout";
import { connected as Booking } from "./booking";
import RelatedTour from "./relatedTour";
import styles from "./styles";
import { createListUrl, createDetailUrlFromResource } from "../../../../server/utils/createUrls";
import { poiTypes } from "../../../../server/lib/open-planet/poiTypes";
import DataLayer from "../../shared/components/dataLayer";
import editUrl from "../utils/editUrl";
import cleanUp from "../utils/cleanUp";
import { getToursAndActivities } from "../selectors/selectActivity";
import { bookmarkButtonGtmClasses } from "../utils/gtm";

/**
 * Renders a bookable restaurant POI
 */
export class RestaurantPoiComponent extends React.Component {
  constructor() {
    super();

    this.state = {
      nearby: false,
      hideMap: false,
    };

    this.getPoiUrl = this.getPoiUrl.bind(this);
    this.enterNearbyPlaces = this.enterNearbyPlaces.bind(this);
    this.leaveNearbyPlaces = this.leaveNearbyPlaces.bind(this);
  }

  shouldComponentUpdate(
    nextProps,
    nextState,
  ) {
    return shallowCompare(this, nextProps, nextState);
  }

  getPoiUrl() {
    const { poi, params, breadcrumb } = this.props;

    return createDetailUrlFromResource(poi, breadcrumb.map(b => b.title), params.placeId);
  }

  enterNearbyPlaces() {
    this.setState({
      hideMap: true,
    });
  }

  leaveNearbyPlaces(props) {
    this.setState({
      hideMap: (props.currentPosition === "above"),
    });
  }

  render() {
    const { poi, place, images, activity, tour, params, breadcrumb } = this.props;
    const subtype = poiTypes[poi.poiType.toLowerCase()].displayName;

    if (poi.review) {
      Object.keys(poi.review).forEach((review) => {
        poi.review[review] = cleanUp.removeBackslash(poi.review[review]);
      });
    }

    const tinyHeading = {
      level: 4,
      size: "tiny",
      weight: "thick",
      importance: "high",
      caps: true,
    };

    const name = poi.name;
    const poiId = `poi-${poi.id}`;
    const categoryLink = createListUrl({
      resource: poi.type,
      kind: poi.poiType,
      breadcrumb: breadcrumb.map(b => b.title),
      placeId: place.id,
    });

    const currentBreadcrumb = {
      title: subtype,
      href: categoryLink,
    };

    return (
      <div className="PageContainer" id={poiId} key={poiId}>
        <DataLayer
          data={{
            cd26_PartnerOfferingDetails: name,
            cd27_PartnerOfferingKind: poi.subtypes.join(","),
            cd28_PartnerName: "",
          }}
        />

        <Helmet
          meta={[
            { name: "op-subtype", class: "swiftype", "data-type": "string", content: subtype },
            { name: "op-type-poi", class: "swiftype", "data-type": "integer", content: "100" },
          ]}
        />

        <Breadcrumbs
          links={[...breadcrumb, currentBreadcrumb]}
        />

        <div className="Page Page--detail">
          <PageHeader
            heading={name}
            title={`${place.name} ${subtype}`}
            titleHref={categoryLink}
            topChoice={poi.poiAttributes && poi.poiAttributes.length &&
              poi.poiAttributes[0].name === "top_choice"}
            type={poi.subtypes[0]}
            place={poi.containingPlace.name}
            alignment="center"
            contained
          />

          <div className="PageContent">
            <div className="Actions">
              <StyleRoot>
                <ShareButton
                  shareText={name}
                  getPoiUrl={this.getPoiUrl}
                  showToast={this.props.showToast}
                />

              </StyleRoot>

              {this.props.marked !== null &&
                <div>
                  <BookmarkButton
                    className={bookmarkButtonGtmClasses(this.props.marked)}
                    onClick={this.props.onClickBookmark}
                    marked={this.props.marked}
                  />

                  <FeatureDiscover
                    featureName="firstPoiSeen"
                    removeOnOuterClick
                    removeOnNavigate
                  >
                    <DiscoveryFlyout
                      headline="Save this place"
                      description="Tap the bookmark to save and organize your interests into lists."
                      style={styles.featureDiscovery.button}
                    />
                  </FeatureDiscover>
                </div>
              }
            </div>

            {images && images.length > 0 && this.props.mobile &&
              <ImagesContainer images={images} mobile />
            }

            <aside className="PageAside" data-hide-map={this.state.hideMap}>
              <Sticky
                enabled={!this.props.mobile}
                top={30}
                bottomBoundary={`#${poiId} .PageContent`}
              >
                {!this.props.mobile && false &&
                  <Booking
                    type="restaurant"
                  />
                }

                <EssentialInfo
                  poi={poi}
                  place={place}
                  mobile={this.props.mobile}
                  links={breadcrumb}
                />

                {(poi.telephone || poi.url) &&
                  <ContactBar
                    phone={poi.telephone}
                    website={poi.url}
                  />
                }

                {this.props.mobile && false &&
                  <Booking
                    type="restaurant"
                  />
                }
              </Sticky>
            </aside>

            <article className="PageArticle">
              {images && images.length > 0 && !this.props.mobile &&
                <ImagesContainer images={images} />
              }

              <div className="PageArticle-content">
                <article>
                  {poi.review &&
                    <ContentBlock
                      heading=""
                      htmlContent={poi.review.essential}
                    />}

                  {activity &&
                    <Callout
                      type={activity.type}
                      heading={activity.name}
                      slug={createDetailUrlFromResource(
                        activity.poi,
                        breadcrumb.map(b => b.title),
                        params.placeId
                      )}
                      price={{
                        currency: activity.currency,
                        amount: activity.price,
                      }}
                      image={activity.image}
                      category={activity.category}
                    />}

                  {poi.review && poi.review.extension && <ContentBlock
                    heading=""
                    htmlContent={poi.review.extension}
                  />}

                  <footer className="ContentFooter">
                    <div className="ContentFooter-text">
                      <EditLink url={editUrl(poi, place, breadcrumb)} />
                    </div>
                  </footer>
                </article>

                <aside>
                  {poi.location &&
                    <section className="ContentSection ContentSection-nearbyPlaces">
                      <ContentHeader
                        title="Location &amp; nearby places"
                        heading={tinyHeading}
                        border="bottom"
                      />

                      <NearbyPlaces
                        currentPlace={poi}
                        mapId={`leaflet-map-container-${poi.id}`}
                        relatedPlaceId={params.placeId}
                      />

                      <Waypoint
                        onEnter={this.enterNearbyPlaces}
                        onLeave={this.leaveNearbyPlaces}
                        fireOnRapidScroll
                      />
                    </section>
                  }

                  {tour &&
                    <section className="ContentSection ContentSection-relatedTour">
                      <ContentHeader
                        title="Related Tour"
                        heading={tinyHeading}
                        border="bottom"
                        moreLink={{
                          href: createListUrl({
                            placeId: place.id,
                            resource: "partner_activity",
                            kind: "tours",
                            breadcrumb: breadcrumb.map(b => b.title),
                          }),
                          size: "small",
                          children: "View all tours",
                        }}
                      />

                      <RelatedTour
                        title={tour.name}
                        slug={createDetailUrlFromResource(
                          tour.poi,
                          breadcrumb.map(b => b.title),
                          params.placeId
                        )}
                        tripLength={tour.duration}
                        price={{
                          amount: tour.price,
                          currency: tour.currency,
                        }}
                        image={tour.image}
                        destination={`${tour.departs} to ${tour.arrives}`}
                        reviews={tour.reviews}
                      />
                    </section>
                  }
                </aside>
              </div>
            </article>
          </div>
        </div>
      </div>
    );
  }
}

RestaurantPoiComponent.propTypes = {
  /**
   * The POI for this component
   */
  poi: PropTypes.object.isRequired,

  /**
   * The place data for the POI
   */
  place: PropTypes.object.isRequired,

  /**
   * An array of images
   */
  images: PropTypes.array.isRequired,

  /**
   * The breadcrumbs for this component
   */
  breadcrumb: PropTypes.array.isRequired,

  /**
   * Whether or not the layout should be formatted for mobile screen sizes
   */
  mobile: PropTypes.bool,

  /**
   * Activity POI related to current restaurant POI
   */
  activity: PropTypes.object,

  /**
   * Tour POI related to current restaurant POI
   */
  tour: PropTypes.object,

  /**
   * React Router params
   */
  params: PropTypes.object,
  onClickBookmark: PropTypes.func,
  showToast: PropTypes.func,
  marked: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  mobile: state.mobile.isMobile,
  activity: getToursAndActivities(state).activity,
  tour: getToursAndActivities(state).tour,
  breadcrumb: state.breadcrumb,
});

const connected = connect(mapStateToProps)(RestaurantPoiComponent);
export { connected };


// WEBPACK FOOTER //
// ./assets/pois/components/restaurant.jsx